import { memo } from 'react';
import { cn } from '@trotroways/utils';
import type { Station } from '@/lib/api';

interface StationPinProps {
  station: Station;
  isSelected?: boolean;
  showLabel?: boolean;
  isVisible?: boolean;
  onClick?: () => void;
}

function getDeterministicLabelSide(stationId: string): 'left' | 'right' {
  let hash = 0;
  for (let i = 0; i < stationId.length; i += 1) {
    hash = (hash << 5) - hash + stationId.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) % 2 === 0 ? 'left' : 'right';
}

export const StationPin = memo(function StationPin({
  station,
  isSelected = false,
  showLabel = true,
  isVisible = true,
  onClick,
}: StationPinProps) {
  const bubbleSize = isSelected ? 64 : 16;
  const labelSide = getDeterministicLabelSide(station.id);
  const labelStyles = isSelected
    ? {
        top: bubbleSize + 14,
        left: '50%',
        transform: 'translateX(-50%)',
        textAlign: 'center' as const,
        maxWidth: 140,
        whiteSpace: 'normal' as const,
        wordBreak: 'normal' as const,
        overflowWrap: 'break-word' as const,
      }
    : labelSide === 'left'
      ? {
        top: Math.max(0, Math.round((bubbleSize - 28) / 2)),
        right: bubbleSize + 10,
        textAlign: 'right' as const,
        maxWidth: 130,
        whiteSpace: 'normal' as const,
        wordBreak: 'normal' as const,
        overflowWrap: 'break-word' as const,
      }
      : {
        top: Math.max(0, Math.round((bubbleSize - 28) / 2)),
        left: bubbleSize + 10,
        textAlign: 'left' as const,
        maxWidth: 130,
        whiteSpace: 'normal' as const,
        wordBreak: 'normal' as const,
        overflowWrap: 'break-word' as const,
      };

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={station.name}
      className={cn(
        'relative inline-flex cursor-pointer appearance-none flex-col items-center overflow-visible border-0 bg-transparent p-0 text-left leading-none transition-[opacity,transform] duration-300 ease-out',
        isSelected && 'z-10',
      )}
      style={{
        touchAction: 'manipulation',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(6px) scale(0.9)',
        pointerEvents: isVisible ? 'auto' : 'none',
      }}
    >
      <div
        className="relative flex flex-col items-center overflow-visible"
        style={{ width: bubbleSize, minHeight: bubbleSize + (isSelected ? 54 : 10) }}
      >
        {showLabel ? (
          <div
            className="absolute text-[10px] font-medium leading-[1.25] tracking-[0.01em] text-white/90 transition-all duration-300 ease-out"
            style={{
              textShadow: '0 1px 3px rgba(0,0,0,0.75)',
              ...labelStyles,
            }}
          >
            {station.name}
          </div>
        ) : null}
        <div
          className="flex shrink-0 items-center justify-center rounded-full"
          style={{
            width: bubbleSize,
            height: bubbleSize,
            background:
              'radial-gradient(circle at 35% 32%, #d8d8d8, #909090 55%, #585858)',
            boxShadow:
              '0 3px 10px rgba(0,0,0,0.55), inset 0 1px 3px rgba(255,255,255,0.45), 0 0 0 1.5px rgba(0,0,0,0.35)',
            transition:
              'width 0.38s cubic-bezier(0.34,1.45,0.64,1), height 0.38s cubic-bezier(0.34,1.45,0.64,1), transform 0.38s cubic-bezier(0.34,1.45,0.64,1)',
          }}
        >
          <div
            className="rounded-full"
            style={{
              width: '44%',
              height: '44%',
              minWidth: 6,
              minHeight: 6,
              background: 'radial-gradient(circle at 38% 36%, #555, #0d0d0d)',
              boxShadow:
                'inset 0 1px 4px rgba(0,0,0,0.9), 0 1px 1px rgba(255,255,255,0.12)',
              transition:
                'width 0.38s cubic-bezier(0.34,1.45,0.64,1), height 0.38s cubic-bezier(0.34,1.45,0.64,1)',
            }}
          />
        </div>
        <div
          className="block rounded-[1px]"
          style={{
            width: isSelected ? 2 : 1.5,
            height: isSelected ? 8 : 5,
            background:
              'linear-gradient(to bottom, rgba(90,90,90,0.8), rgba(50,50,50,0.2))',
          }}
        />
        <div
          className="block rounded-full"
          style={{
            width: isSelected ? 7 : 5,
            height: isSelected ? 3 : 2,
            background: 'rgba(0,0,0,0.28)',
            filter: 'blur(1px)',
          }}
        />
      </div>
    </button>
  );
});
